export const ASSET_TYPE = {
  SERIES: "series",
  SEASON: "season",
  EPISODE: "episode",
  MOVIE: "movie",
  PROMO: "promo",
  ID: "idType",
  DMI_TITLE: "dmiTitle",
  CANTEMO: "cantemo"
};

export const RECORD_TYPES = [
  {
    label: "ID",
    value: ASSET_TYPE.ID,
  },
  {
    label: "Series",
    value: ASSET_TYPE.SERIES,
  },
  {
    label: "Season",
    value: ASSET_TYPE.SEASON,
  },
  {
    label: "Episode",
    value: ASSET_TYPE.EPISODE,
  },
  {
    label: "Features",
    value: ASSET_TYPE.MOVIE,
  },
  {
    label: "Promo",
    value: ASSET_TYPE.PROMO,
  },
  {
    label: "DMI Title Name",
    value: ASSET_TYPE.DMI_TITLE,
  },
  {
    label: "Cantemo",
    value: ASSET_TYPE.CANTEMO,
  }
];

export type CantemoFilters = {
  title: string;
  episodeTitle: string;
  episodeNumber: string;
  generationType: string[];
  language: string[];
  fileFormat: string[];
  frameRate: string[];
  mimeType: string[];
};

export const EMPTY_CANTEMO_FILTERS: CantemoFilters = {
  title: "",
  episodeTitle: "",
  episodeNumber: "",
  generationType: [],
  language: [],
  fileFormat: [],
  frameRate: [],
  mimeType: [],
};

export const CANTEMO_TEXT_FIELDS = [
  { key: "title", label: "Title Name", placeholderKey: "placeholder" },
  { key: "episodeTitle", label: "Episode Title Name", placeholderKey: "placeholder" },
  { key: "episodeNumber", label: "Episode Number" },
];

export const CANTEMO_DROPDOWN_FIELDS = [
  { key: "generationType", label: "Media Type" },
  { key: "language", label: "Language" },
  { key: "fileFormat", label: "File Format" },
  { key: "frameRate", label: "Frame Rate" },
  { key: "mimeType", label: "Video Type" },
];

const CANTEMO_LANGUAGE_SOURCE_VALUES = [
  "ar",
  "BAHA",
  "bn",
  "castilian",
  "CHIM",
  "cs",
  "da",
  "DAN",
  "danish",
  "Danish",
  "de",
  "en",
  "ENG",
  "eng",
  "english",
  "es",
  "fi",
  "finnish",
  "fr",
  "french",
  "german",
  "he",
  "HEBR",
  "HIN",
  "it",
  "italian",
  "ja",
  "japanese",
  "jp",
  "ko",
  "korean",
  "latin-spanish",
  "MALA",
  "no",
  "NORW",
  "norwegian",
  "pa",
  "pt",
  "RUS",
  "SCC_23.98",
  "SCC_combined",
  "SCC_NTSC_NDF",
  "SMPTE_TT_5.16_23.98",
  "SMPTE_TT_5.16_NTSC_NDF",
  "SMPTE_TT_5.16_PAL",
  "SMPTE_TT_combined",
  "sv",
  "SWE",
  "swedish",
  "TAG",
  "TAML",
  "THAI",
  "ur",
  "VIET",
  "zu",
];

const CANTEMO_LANGUAGE_ALIAS_TO_CODE = {
  en: "en",
  eng: "en",
  english: "en",
  da: "da",
  dan: "da",
  danish: "da",
  de: "de",
  german: "de",
  es: "es",
  castilian: "es",
  "latin-spanish": "es",
  fi: "fi",
  finnish: "fi",
  fr: "fr",
  french: "fr",
  he: "he",
  hebr: "he",
  it: "it",
  italian: "it",
  ja: "ja",
  japanese: "ja",
  jp: "ja",
  ko: "ko",
  korean: "ko",
  no: "no",
  norw: "no",
  norwegian: "no",
  sv: "sv",
  swe: "sv",
  swedish: "sv",
};

export const CANTEMO_LANGUAGE_VARIANTS = CANTEMO_LANGUAGE_SOURCE_VALUES.reduce((groups, value) => {
  const code = CANTEMO_LANGUAGE_ALIAS_TO_CODE[value.trim().toLowerCase()] || value;
  if (!groups[code]) {
    groups[code] = [];
  }
  groups[code].push(value);
  return groups;
}, {} as Record<string, string[]>);

export const CANTEMO_DROPDOWN_OPTIONS = {
  generationType: ["Backlot", "EST", "FLM", "RAW"],
  language: Object.keys(CANTEMO_LANGUAGE_VARIANTS),
  fileFormat: [
    "Audio Media",
    "CC / Subtitle Media",
    "Misc File Media",
    "MPEG Media",
    "ProRes Media",
  ],
  frameRate: ["23.98", "24", "25", "29.97", "50", "59.94"],
  mimeType: [
    "application/rtf",
    "application/vnd.ms-pki.stl",
    "application/x-font-ttf",
    "application/xml",
    "application/zip",
    "text/plain",
    "video/mp4",
    "video/mpeg",
    "video/quicktime",
    "video/x-m4v",
    "video/x-mxf-mpeg2video-pcm_s24le",
  ],
};

export const MEDIA_SEARCH = {
  limit: 30,
  throttleTime: 200,
  minimumKeyStroke: 2,
  placeholder: "What can we help you find?",
  storageKey: "mediaRequestPage_brandNameMapping",
  recordTypeFormLabel: "Select Record Type",
  dashboardHeader: "Media Request",
  idTypeFormLabel: "Select ID Type",
  cantemoDropdownPlaceholder: "-----",
  cantemoSelectAll: "Select all",
  cantemoPageSize: 50,
  cantemoPrev: "Prev",
  cantemoNext: "Next",
  idTypeValuePlaceholder: "Enter IDs using commas, semicolons, or separate lines. You can also copy and paste from a spreadsheet. Only 10 unique IDs allowed at a time.",
  cutIdTypePlaceholder: "Enter IDs using commas, semicolons, or separate lines. You can also copy and paste from a spreadsheet. Only 5 unique IDs allowed at a time.",
};

export const ASSET_TABLE_COLUMNS = [
  "Network",
  "Length",
  "Case Type",
  "Format",
  "Frame Rate"
];

export const ASSET_ROW_KEYS = [
  "controllingNetwork",
  "compositionInterval.durationSeconds",
  "caseType",
  "format",
  "components"
];

export const MEDIA_CELL_WIDTH_KEYS = ["format", "components"];

export const PAGE_ERROR = {
  title: "Network Error",
  message:
    "There was an issue loading the page data. Please try reloading the page.",
};

export const CASE_TYPE_MAPPING = {
  "alternate network":
    "Program version created for a different network than the Program Master File - changes could be drastic (like a different script/host/editing style or could be subtle like increasing blurring for a network whose audience skews younger)",
  "alternate talent":
    "Version of a Program Master File that has a different host or different narrator than the main version.",
  "compliance": "Specific Version with edits to meet compliance rules",
  "duration change":
    "Time-compressed or edited version that is either shorter or longer than the source case",
  "evergreen":
    "Full version of the program intended for repeated broadcast (needed when premiere version has content that is not cleared in perpetuity). Cleared worldwide in perpetuity. (Evergreen to explicitly indicate the absence of rights restrictions and/or any temporal content)",
  "general":
    "Full version of the program that doesn’t fit into any other case type",
  "international":
    "Program version content that is for international broadcast or digital playout",
  "legacy":
    "Program version content that was created from legacy systems prior to WBD Merger",
  "multiplatform":
    "Linear program formatted for use on digital platforms. Will allow selection of intended platforms",
  "nl platform":
    "Program master file specific to a “digital” platform. Will allow selection of intended platforms and platform specific details (i.e. Snapchat; Top)",
  "promotional":
    "Content with additional promotional content, for example an episode that plugs a specific release date of a movie. This can be used to distinguish these versions; one with the promo from the one without",
  "reformat":
    "A re-edit of the program that doesn’t meet any other categories (i.e. add snap-ins to make a longer version). To denote when another asset was cut from an original episode E.g. Recipe Cutdown",
  "rights restricted":
    "There is some type of restricted element in the program (i.e. music, talent, footage, and so on). This is NOT a substitute for Rights Summary or any other Rights data. If there are Elemental restrictions for 1 Version and not another - use of this Case Type helps to disambiguate the Version for the Producer uploading both Versions",
  "sponsored":
    "Program master file where sponsorship is baked in. Will allow entry of intended sponsor",
  "supply":
    "Primarily used for Acquisitions to indicate asset should not go to air and requires editing (i.e. EHM Intl versions)",
  "temporary":
    "Unique Version level metadata & show code to get premiere; indicates the content is same and prevents duplicate selling on NL platforms; pop-ups, Added content like audition tape, behind the scenes, digital recipe, social media reaction. Host wraps, in memoriam graphic, etc.",
  "topical update":
    "This can happen due to rights concerns with talent on a specific platform or to customize for a certain region.",
  "trailers":
    "Trailers for specific episode. Can contain textless deliverables within Case. Need to be set up as their own Deal Program with episodes underneath.",
};

export const ID_TYPES = [
  {
    label: "CID",
    value: {
      queryType: "alternateId", 
      namespace: ["urn:warnermedia:cid.turner.com", "urn:wbd:identifier:warnermedia:content-id"],
    },
  },
  {
    label: "Gabriel ID",
    value: {
      queryType: "alternateId", 
      namespace: ["urn:wbd:identifier:gabriel:house-number"],
    },
  },
  {
    label: "Title ID",
    value: {
      queryType: "alternateId", 
      namespace: ["urn:warnermedia:titleid.titles.turner.com","urn:wbd:identifier:turner:title-id", "urn:wbd:identifier:turner:edit-id"],
    },
  },
  {
    label: "PAID",
    value: {
      queryType: "alternateId", 
      namespace: ["urn:wbd:identifier:program-inventory:paid"],
    },
  },
  {
    label: "PAFI",
    value: {
      queryType: "alternateId", 
      namespace: ["urn:warnermedia:pafi.prime.hbo.com"],
    },
  },
  {
    label: "PAVI",
    value: {
      queryType: "alternateId", 
      namespace: ["urn:warnermedia:pavi.prime.hbo.com", "urn:wbd:identifier:hbo:program-id", "urn:wbd:identifier:hbo:pavi-id"],
    },
  },
  {
    label: "Program ID",
    value: {
      queryType: "programId", 
      namespace: ["urn:wbd:identifier:program-inventory:id"],
    },
  },
  {
    label: "ISCI",
    value: {
      queryType: "alternateId", 
      namespace: ["urn:wbd:identifier:cat:isci"],
    },
  },
  {
    label: "Manifestation ID",
    value: {
      queryType: "manifestationId", 
      namespace: ["urn:warnermedia:manifestation.warnermedia.com"],
    },
  },
  {
    label: "Edit ID",
    value: {
      queryType: "editId", 
      namespace: ["urn:wbd:identifier:program-inventory:id", "urn:warnermedia:pavi.prime.hbo.com", "urn:wbd:identifier:hbo:program-id", "urn:wbd:identifier:hbo:pavi-id", "urn:warnermedia:titleid.titles.turner.com","urn:wbd:identifier:turner:title-id", "urn:wbd:identifier:turner:edit-id", "urn:warnermedia:cid.turner.com", "urn:wbd:identifier:warnermedia:content-id"]
    },
  },
  {
    label: "Edit SCRID",
    value: {
      queryType: "alternateId", 
      namespace: ["urn:wbd:identifier:program-inventory:scrid"],
    },
  },
  {
    label: "EIDR Edit ID",
    value: {
      queryType: "alternateId", 
      namespace: ["urn:wbd:identifier:eidr:edit-id"],
    },
  },
  {
    label: "HBO Promo PAVI",
    value: {
      queryType: "hboPromo", 
      namespace: ["pavi", "pafi"],
    },
  },
  {
    label: "DMI CID",
    value: {
      queryType: "dmi", 
      namespace: ["dmicid"],
    },
  },
  {
    label: "DMI Title ID",
    value: {
      queryType: "dmi", 
      namespace: ["dmititleid"],
    },
  },
  {
    label: "Cantemo Focus ID",
    value: {
      queryType: "cantemo", 
      namespace: ["cantemofocusid"],
    },
  },
  {
    label: "BIM CID",
    value: {
      queryType: "bim", 
      namespace: ["bimcid"],
    },
  },
  {
    label: "CUT CID",
    value: {
      queryType: "cut", 
      namespace: ["cutcid"],
    },
  }
];

export const PROXY_MANIFESTATION_TYPES = [
  "urn:warnermedia:manifestation:type:mam-proxy",
  "urn:warnermedia:manifestation:type:qc-proxy"
];

export const ID_TYPES_VALUES = {
  "CUT_CID": "cutcid"
}
